import { useLingui } from '@lingui/react/macro'
import { ChooseEditScopePanel } from 'features/knowledgeDocumentViewer/components/metadata/ChooseEditScopePanel'
import { DocumentMetadataContent } from 'features/knowledgeDocumentViewer/components/metadata/DocumentMetadataContent'
import { MetadataEditFormChrome } from 'features/knowledgeDocumentViewer/components/metadata/MetadataEditFormChrome'
import { useDocumentMetadataEditForm } from 'features/knowledgeDocumentViewer/hooks/useDocumentMetadataEditForm'
import { useDocumentRevisionFamily } from 'features/knowledgeDocumentViewer/hooks/useDocumentRevisionFamily'
import {
  saveMetadataEditWithScope,
  useMetadataEditScopeFlow,
} from 'features/knowledgeDocumentViewer/hooks/useMetadataEditScopeFlow'
import type {
  DocumentMetadataUserEditEntry,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditEntry,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import type { FamilyDocumentMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyDocumentMetadataUserEditPuts'
import { withoutDocumentRevisionFields } from 'features/knowledgeDocumentViewer/utils/documentMetadataFieldKeys'
import type { DocumentRevisionFamilyMember } from 'features/knowledgeDocumentViewer/utils/documentRevisionFamily'
import { mergePageMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import type { RevisionFamilyMemberView } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import React, { useCallback } from 'react'

function resolveCascadeSystemPage(
  member: DocumentRevisionFamilyMember,
  pageNum: number | undefined,
  currentPageEntry: SystemDocumentPageEntry | undefined,
  pagesByBqDocumentId: ReadonlyMap<string, SystemDocumentPageEntry>
): SystemDocumentPageEntry | undefined {
  if (member.isCurrent && currentPageEntry !== undefined) {
    return currentPageEntry
  }
  if (pageNum === undefined) return undefined
  return pagesByBqDocumentId.get(member.bqDocumentId)
}

export type DocumentMetadataEditSectionProps = {
  document: SystemDocumentEntry | undefined
  documentUserEdit: DocumentMetadataUserEditEntry | undefined
  isLoading: boolean
  canEdit: boolean
  isSaving: boolean
  pageNum?: number
  pageEntry?: SystemDocumentPageEntry
  pageUserEdit?: DocumentPageMetadataUserEditEntry
  onSave: (values: DocumentMetadataUserEditValues) => Promise<void>
  onSaveFamily: (
    values: DocumentMetadataUserEditValues,
    members: readonly FamilyDocumentMetadataSaveMember[]
  ) => Promise<void>
  onEditingChange?: (isEditing: boolean) => void
  onSelectVersion?: (member: RevisionFamilyMemberView) => void
  onOpenInNewTab?: (member: RevisionFamilyMemberView) => void
  includeInternalIds?: boolean
  hideRevision?: boolean
}

export function DocumentMetadataEditSection({
  document,
  documentUserEdit,
  isLoading,
  canEdit,
  isSaving,
  pageNum,
  pageEntry,
  pageUserEdit,
  onSave,
  onSaveFamily,
  onEditingChange,
  onSelectVersion,
  onOpenInNewTab,
  includeInternalIds = false,
  hideRevision = false,
}: DocumentMetadataEditSectionProps): React.ReactElement {
  const { t } = useLingui()
  const userEditValues = documentUserEdit?.entry.data.values
  const pageBaselineValues = mergePageMetadataValues(
    pageEntry?.entry.data,
    pageUserEdit?.entry.data.values
  )
  const currentBqDocumentId = document
    ? getSystemDocumentBqId(document)
    : undefined

  const {
    members,
    priorEditsByDocumentEntryId,
    pagesByBqDocumentId,
    priorPageEditsByBqDocumentId,
    mutatePriorEdits,
    arePriorEditsReady,
    hasRevisionFamily,
    familySize,
    isLoading: isFamilyLoading,
  } = useDocumentRevisionFamily({
    document,
    documentUserEdit,
    currentBqDocumentId,
    cascadePageNum: pageNum,
    enabled: canEdit,
  })

  const {
    isChoosingScope,
    editScope,
    notifyBusy,
    formActionsRef,
    handleEditClick,
    handleCancelScope,
    handleChooseScope,
    handleChangeScope,
    handleCancelEdit,
  } = useMetadataEditScopeFlow({
    resetKey: `${document?.entry.id ?? ''}:${documentUserEdit?.entry.id ?? ''}`,
    hasRevisionFamily,
    arePriorEditsReady,
    onEditingChange,
  })

  const resolveMemberPriorEdit = useCallback(
    (member: DocumentRevisionFamilyMember) => {
      if (member.isCurrent && documentUserEdit !== undefined) {
        return documentUserEdit
      }
      return priorEditsByDocumentEntryId.get(member.memberEntryId)
    },
    [documentUserEdit, priorEditsByDocumentEntryId]
  )

  const handleFormSave = useCallback(
    async (values: DocumentMetadataUserEditValues) => {
      await saveMetadataEditWithScope({
        editScope,
        hasRevisionFamily,
        values,
        familyMembers: members.map((member) => ({
          bqDocumentId: member.bqDocumentId,
          systemDocument: member.systemDocument,
          priorEdit: resolveMemberPriorEdit(member),
          pageNum,
          systemPage: resolveCascadeSystemPage(
            member,
            pageNum,
            pageEntry,
            pagesByBqDocumentId
          ),
          pagePriorEdit:
            member.isCurrent && pageUserEdit !== undefined
              ? pageUserEdit
              : priorPageEditsByBqDocumentId.get(member.bqDocumentId),
        })),
        onSave,
        onSaveFamily,
        mutatePriorEdits,
      })
    },
    [
      editScope,
      hasRevisionFamily,
      members,
      mutatePriorEdits,
      onSave,
      onSaveFamily,
      pageEntry,
      pageNum,
      pageUserEdit,
      pagesByBqDocumentId,
      priorPageEditsByBqDocumentId,
      resolveMemberPriorEdit,
    ]
  )

  const {
    isEditing,
    editableFields,
    lockedLabels,
    showLeaveFamilyWarning,
    crossSurfaceCascadeWarning,
    beginEdit,
    cancelEdit,
    setFieldValue,
    saveEdit,
  } = useDocumentMetadataEditForm({
    document,
    userEditValues,
    userEditEntryId: documentUserEdit?.entry.id,
    editScope,
    pageBaselineValues,
    onSave: handleFormSave,
    onEditingChange: notifyBusy,
  })

  formActionsRef.current = { beginEdit, cancelEdit }

  const handleSaveClick = useCallback(() => {
    void saveEdit()
  }, [saveEdit])

  if (isChoosingScope) {
    return (
      <ChooseEditScopePanel
        familySize={familySize}
        currentFilename={document?.entry.data.filename}
        members={members}
        familyScopeDisabled={!arePriorEditsReady}
        metadataSurface='document'
        onCancel={handleCancelScope}
        onChooseScope={handleChooseScope}
        onSelectVersion={onSelectVersion}
        onOpenInNewTab={onOpenInNewTab}
      />
    )
  }

  return (
    <MetadataEditFormChrome
      title={t`Document Details`}
      canEdit={canEdit}
      isEditing={isEditing}
      isLoading={isLoading}
      isSaving={isSaving}
      isFamilyLoading={isFamilyLoading}
      editAriaLabel={t`Edit document metadata`}
      cancelAriaLabel={t`Cancel editing document metadata`}
      saveAriaLabel={t`Save document metadata`}
      confirmMessage={t`Saving these edits confirms that all document information is correct.`}
      saveLabel={t`Save document data`}
      familySaveLabel={t`Save all ${familySize} revisions`}
      leaveFamilyWarning={
        showLeaveFamilyWarning
          ? t`Changing Drawing # removes this document from the revision family.`
          : undefined
      }
      crossSurfaceCascadeWarning={crossSurfaceCascadeWarning}
      editScope={editScope}
      hasRevisionFamily={hasRevisionFamily}
      familySize={familySize}
      currentFilename={document?.entry.data.filename}
      metadataSurface='document'
      editableFields={
        hideRevision
          ? withoutDocumentRevisionFields(editableFields)
          : editableFields
      }
      lockedLabels={lockedLabels}
      onEditClick={handleEditClick}
      onCancelEdit={handleCancelEdit}
      onSaveEdit={handleSaveClick}
      onChangeScope={handleChangeScope}
      onFieldChange={setFieldValue}>
      <DocumentMetadataContent
        document={document}
        isLoading={isLoading}
        userEditValues={userEditValues}
        includeInternalIds={includeInternalIds}
        hideRevision={hideRevision}
      />
    </MetadataEditFormChrome>
  )
}
